import { Button, Grid, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import Algorithms from '../../constants/Algorithms';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import CustomizeProductFeedModal from '../Modals/CustomizeProductFeedModal';
import SearchableCombobox from '../SearchableCombobox';
import Spinner from '../Spinner';

const ProductFeedSelector = ({
  accountId,
  domainId,
  productFeedId,
  onSelect,
  onInitialLoad,
  scope,
  forType,
  optional,
  compact,
}) => {
  const fetch = useFetch();
  const [error, setError] = useState();
  const [productFeeds, setProductFeeds] = useState();
  const [dirty, setDirty] = useState(false);
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/productFeeds`)
      .then(({ productFeeds }) => {
        const scopeArray = Array.isArray(scope) ? scope : scope ? [scope] : null;
        const filtered = productFeeds.filter((f) => !scopeArray || scopeArray.includes(f.scope));
        setProductFeeds(filtered);
        if (onInitialLoad && productFeedId) {
          const initialProductFeed = filtered.find((f) => f.id === productFeedId);
          if (initialProductFeed) {
            onInitialLoad(initialProductFeed);
          }
        }
      })
      .catch((error) => {
        setError(error);
      });
  }, [fetch, accountId, domainId, scope, customizeModalOpen]);

  if (productFeeds === undefined) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  let options = productFeeds.map((f) => {
    const rules = f.rules && f.rules.length > 0 ? f.rules.filter((r) => r.mode !== 'off') : [];

    return {
      value: f.id.toString(),
      label: `${f.name} (Algorithm: ${Algorithms[f.algorithm].name} | Rules: ${
        rules.length > 0 ? rules.map((r) => r.name).join(' ') : 'None'
      })`,
    };
  });

  if (optional) {
    options = [{ value: '', label: 'None' }, ...options];
  }

  return (
    <>
      <CustomizeProductFeedModal
        isOpen={customizeModalOpen}
        close={() => setCustomizeModalOpen(false)}
        productFeed={productFeeds.find((p) => p.id === productFeedId) || productFeeds[0]}
        onSelect={onSelect}
        forType={forType}
      />
      {/* compact: one dense row with a subtle Customize, for settings lists. */}
      <Grid gutter={compact ? 'xs' : undefined} align={compact ? 'center' : undefined}>
        <Grid.Col span={compact ? 'auto' : { base: 12, md: 9 }}>
          <SearchableCombobox
            options={options}
            value={productFeedId?.toString()}
            onChange={(value) => {
              setDirty(true);
              const id = isNaN(value) || value === '' ? null : parseInt(value);
              const selectedProductFeed = productFeeds.find((f) => f.id === id);
              onSelect(id, selectedProductFeed);
            }}
            placeholder="(Select product feed)"
            searchPlaceholder="Search product feeds..."
            emptyMessage="No product feeds found"
            error={dirty && !productFeedId && !optional}
            onBlur={() => setDirty(true)}
          />
        </Grid.Col>
        <Grid.Col span={compact ? 'content' : { base: 12, md: 3 }}>
          <Button
            fullWidth={!compact}
            variant={compact ? 'subtle' : undefined}
            size={compact ? 'xs' : undefined}
            disabled={dirty && !productFeedId}
            onClick={() => {
              setCustomizeModalOpen(true);
            }}
          >
            Customize
          </Button>
        </Grid.Col>
      </Grid>
      {dirty && !productFeedId && !optional && (
        <Text size="sm" c="red">
          Product feed is required
        </Text>
      )}
    </>
  );
};

export default ProductFeedSelector;
